import { useEffect, useState, type ReactNode } from 'react';
import type { DirectoryUser } from '@/types';
import { OwnerPicker } from '@/components/OwnerPicker';

/**
 * Reassigns the PORTAL's own ownership record — never MANDW01's. The warning at the bottom is not
 * decoration: it exists so nobody walks away from a successful reassignment believing they have
 * fixed a mail-delivery failure, which this action cannot do on its own. See AdminController.Reassign
 * for why the portal has no write path to the report server's own owner at all.
 */
export function ReassignOwnerDialog({
  title,
  subject,
  busy,
  error,
  onConfirm,
  onCancel
}: {
  title: string;
  /** What is being reassigned — a report name, or "12 selected subscriptions". */
  subject: ReactNode;
  busy: boolean;
  error?: string | null;
  onConfirm: (user: DirectoryUser) => void;
  onCancel: () => void;
}) {
  const [picked, setPicked] = useState<DirectoryUser | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="modal-backdrop" role="presentation" onClick={onCancel}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reassign-owner-title"
        onClick={e => e.stopPropagation()}>
        <h3 id="reassign-owner-title">{title}</h3>
        <p style={{ color: 'var(--kt-grey-700)', fontSize: '.92rem', marginBottom: 12 }}>
          Choose the new portal owner for {subject}.
        </p>

        <OwnerPicker value={picked} onChange={setPicked} />

        <div className="notice notice-warn" style={{ marginTop: 16 }}>
          <strong>This changes the portal&rsquo;s own record only.</strong>
          <div>
            It updates who can manage this subscription here, and who &ldquo;My subscriptions&rdquo; shows it to.
            It does <strong>not</strong> change the Windows account the report server sends mail as — that
            is a separate, report-server-only setting the portal cannot write. If this subscription&rsquo;s mail is
            failing because that account no longer exists, it will keep failing until the report server
            record is fixed there directly (a new stored procedure, or a manual re-owning by the BI team).
          </div>
        </div>

        {error && <div className="error-banner" style={{ marginTop: 12 }}>{error}</div>}

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            className="btn-primary"
            onClick={() => picked && onConfirm(picked)}
            disabled={busy || !picked}>
            {busy ? 'Reassigning…' : 'Reassign'}
          </button>
        </div>
      </div>
    </div>
  );
}
